import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectionStore } from "../../stores/selectionStore";
import { buildBodyParentMap } from "../../utils/bodyHierarchy";
import { getJointMoveDisabledReason } from "../../utils/jointTypeValidation";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { collectDescendantBodyUuids } from "./inspectorTargetUtils";

export function useInspectorReparent({
  activePanel,
  allBodies,
  allJoints,
  isPhysicsRunning,
  resolvedActiveVisualGeom,
  sceneActions,
  selectedBody,
  selectedBodyExists,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedSite,
  selectedSiteRecord,
}) {
  const [inspectorReparentOpen, setInspectorReparentOpen] = useState(false);
  const [inspectorReparentMenuPosition, setInspectorReparentMenuPosition] = useState(null);
  const inspectorReparentRef = useRef(null);
  const inspectorReparentMenuRef = useRef(null);

  const closeInspectorReparent = useCallback(() => {
    setInspectorReparentOpen(false);
    setInspectorReparentMenuPosition(null);
  }, []);

  useEffect(() => {
    closeInspectorReparent();
  }, [
    activePanel,
    closeInspectorReparent,
    selectedBody,
    selectedGeom,
    selectedJoint,
    selectedSite,
    selectedInertial,
  ]);

  useEffect(() => {
    if (!inspectorReparentOpen) return undefined;

    const handlePointerDown = (event) => {
      if (inspectorReparentRef.current?.contains(event.target)) return;
      if (inspectorReparentMenuRef.current?.contains(event.target)) return;
      closeInspectorReparent();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeInspectorReparent();
      }
    };

    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeInspectorReparent, inspectorReparentOpen]);

  const inspectorReparentConfig = useMemo(() => {
    const bodies = allBodies || {};
    const bodyList = Object.values(bodies).filter((body) => body?.uuid);
    const parentMap = buildBodyParentMap(bodies);
    const findBodyForListItem = (listKey, itemUuid) => {
      if (!itemUuid) return null;
      for (const [bodyUuid, body] of Object.entries(bodies)) {
        const list = Array.isArray(body?.[listKey]) ? body[listKey] : [];
        if (list.some((uuid) => String(uuid) === String(itemUuid))) return bodyUuid;
      }
      return null;
    };

    if (activePanel === "body" && selectedBody && selectedBodyExists) {
      const invalidBodyUuids = collectDescendantBodyUuids(selectedBody, bodies);
      const currentParentBodyUuid = parentMap[selectedBody] || null;
      const options = bodyList.filter(
        (body) =>
          body.uuid &&
          !invalidBodyUuids.has(body.uuid) &&
          body.uuid !== selectedBody &&
          body.uuid !== currentParentBodyUuid
      );
      const canMoveToWorld = currentParentBodyUuid != null;
      return {
        visible: true,
        disabled: isPhysicsRunning || (options.length === 0 && !canMoveToWorld),
        label: isPhysicsRunning
          ? "Stop physics to reparent"
          : options.length > 0 || canMoveToWorld
            ? "Reparent"
            : "No reparent targets available",
        options,
        canMoveToWorld,
        onSelect: (newParentBodyUuid) => {
          sceneActions.moveBodyToParent(selectedBody, newParentBodyUuid);
          closeInspectorReparent();
          requestInspectorChromeFlash();
        },
      };
    }

    if ((activePanel === "geom" || activePanel === "visualGeom") && resolvedActiveVisualGeom) {
      const sourceBodyUuid = resolvedActiveVisualGeom.bodyUuid;
      const geomUuid = resolvedActiveVisualGeom.geom?.uuid;
      const options = bodyList.filter((body) => body.uuid && body.uuid !== sourceBodyUuid);
      return {
        visible: Boolean(sourceBodyUuid && geomUuid),
        disabled: isPhysicsRunning || options.length === 0,
        label: isPhysicsRunning
          ? "Stop physics to reparent"
          : options.length > 0
            ? "Reparent"
            : "No reparent targets available",
        options,
        onSelect: (newParentBodyUuid) => {
          sceneActions.moveGeomToBody(sourceBodyUuid, String(geomUuid), newParentBodyUuid);
          const selection = useSelectionStore.getState();
          selection.setLastSelectionSource("sceneTree");
          selection.selectGeom(`${newParentBodyUuid}:${String(geomUuid)}`);
          closeInspectorReparent();
          requestInspectorChromeFlash();
        },
      };
    }

    if (activePanel === "joint" && selectedJoint) {
      const sourceBodyUuid = findBodyForListItem("joints", selectedJoint);
      const joint = allJoints?.[selectedJoint] || null;
      const options = bodyList
        .filter((body) => body.uuid && body.uuid !== sourceBodyUuid)
        .map((body) => {
          const description = getJointMoveDisabledReason({
            joint,
            jointUuid: selectedJoint,
            bodies,
            joints: allJoints,
            sourceBodyUuid,
            targetBodyUuid: body.uuid,
          });
          return description ? { ...body, disabled: true, description } : body;
        });
      const hasEnabledOptions = options.some((body) => !body.disabled);
      return {
        visible: Boolean(sourceBodyUuid),
        disabled: isPhysicsRunning || !hasEnabledOptions,
        label: isPhysicsRunning
          ? "Stop physics to reparent"
          : hasEnabledOptions
            ? "Reparent"
            : options.length > 0
              ? "No valid reparent targets"
              : "No reparent targets available",
        options,
        onSelect: (newParentBodyUuid) => {
          const disabledReason = getJointMoveDisabledReason({
            joint,
            jointUuid: selectedJoint,
            bodies,
            joints: allJoints,
            sourceBodyUuid,
            targetBodyUuid: newParentBodyUuid,
          });
          if (disabledReason) return;
          sceneActions.moveJointToBody(selectedJoint, newParentBodyUuid);
          closeInspectorReparent();
          requestInspectorChromeFlash();
        },
      };
    }

    if (activePanel === "site" && selectedSiteRecord?.uuid) {
      const sourceBodyUuid = findBodyForListItem("sites", selectedSiteRecord.uuid);
      const options = bodyList.filter((body) => body.uuid && body.uuid !== sourceBodyUuid);
      return {
        visible: Boolean(sourceBodyUuid),
        disabled: isPhysicsRunning || options.length === 0,
        label: isPhysicsRunning
          ? "Stop physics to reparent"
          : options.length > 0
            ? "Reparent"
            : "No reparent targets available",
        options,
        onSelect: (newParentBodyUuid) => {
          sceneActions.moveSiteToBody(selectedSiteRecord.uuid, newParentBodyUuid);
          closeInspectorReparent();
          requestInspectorChromeFlash();
        },
      };
    }

    if (activePanel === "inertial") {
      const str = String(selectedInertial || "");
      const sepIdx = str.indexOf(":");
      const sourceBodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
      const inertialUuid = sepIdx > 0 ? str.slice(sepIdx + 1) : null;
      const options = bodyList.filter(
        (body) => body.uuid && body.uuid !== sourceBodyUuid && !body.inertia
      );
      return {
        visible: Boolean(sourceBodyUuid && inertialUuid),
        disabled: isPhysicsRunning || options.length === 0,
        label: isPhysicsRunning
          ? "Stop physics to reparent"
          : options.length > 0
            ? "Reparent to body without inertial"
            : "No target bodies without inertial",
        options,
        onSelect: (newParentBodyUuid) => {
          sceneActions.moveInertiaToBody(sourceBodyUuid, newParentBodyUuid);
          const selection = useSelectionStore.getState();
          selection.setLastSelectionSource("sceneTree");
          selection.selectInertial(`${newParentBodyUuid}:${inertialUuid}`);
          closeInspectorReparent();
          requestInspectorChromeFlash();
        },
      };
    }

    return { visible: false, disabled: true, label: "Reparent" };
  }, [
    activePanel,
    allBodies,
    allJoints,
    closeInspectorReparent,
    isPhysicsRunning,
    resolvedActiveVisualGeom,
    sceneActions,
    selectedBody,
    selectedBodyExists,
    selectedInertial,
    selectedJoint,
    selectedSiteRecord,
  ]);

  return {
    inspectorReparentConfig,
    inspectorReparentMenuPosition,
    inspectorReparentMenuRef,
    inspectorReparentOpen,
    inspectorReparentRef,
    setInspectorReparentMenuPosition,
    setInspectorReparentOpen,
  };
}
